import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { truncate } from 'lodash';
import { ListBase, useListController, usePermissions } from 'react-admin';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';

import { iconNameIconMap, PermissionIconName } from '../../constants/permissionIconNames';
import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';
import { CREATE_ROLE, OutletContext } from './PermissionRolesView';

type PermissionRole = {
  id: string;
  name: string;
  description?: string;
  baseRole: string;
  permissions: {
    name: string;
    attributes?: unknown;
  }[];
  iconName: PermissionIconName;
}

const ButtonRoleBase = ({
  isSelected,
  onClick,
  children,
  disabled,
}: {
  isSelected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  return (
    <Button
      variant={isSelected ? 'contained' : 'outlined'}
      color={isSelected ? 'primary' : 'inherit'}
      sx={{
        maxWidth: 340,
        flexShrink: 0,
        alignItems: 'stretch',
        borderColor: isSelected ? undefined : theme.palette.divider,
        wordBreak: 'break-word',
        '&:not(:hover)': {
          backgroundColor: isSelected ? undefined : theme.palette.background.paper,
        },
      }}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </Button>
  );
};

const ButtonRole = ({
  title,
  subtitle,
  isSelected,
  disabled,
  iconName,
  onClick,
}: {
  isSelected: boolean;
  disabled: boolean;
  title: string;
  subtitle: string;
  iconName?: PermissionIconName;
  onClick: () => void;
}) => (
  <ButtonRoleBase
    isSelected={isSelected}
    onClick={onClick}
    disabled={disabled}
  >
    <Box
      alignItems="flex-start"
      paddingY={4}
      paddingX={2}
      gap={2}
      width="100%"
      display="flex"
      flexDirection="row"
    >
      {/* //TODO: Remove check once we have migrated the data */}
      {iconName && React.createElement(iconNameIconMap[iconName])}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
      >
        <Typography textAlign="left" variant="h4">{title}</Typography>
        <Typography textAlign="left">{subtitle}</Typography>
      </Box>
    </Box>
  </ButtonRoleBase>
);

const CreateButton = ({
  isSelected,
  disabled,
  onClick,
}: {
  isSelected: boolean;
  disabled: boolean;
  onClick: () => void;
}) => (
  <ButtonRoleBase
    isSelected={isSelected}
    onClick={onClick}
    disabled={disabled}
  >
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
      paddingY={4}
      paddingX={2}
      gap={1}
    >
      <AddIcon color={isSelected ? 'inherit' : 'secondary'} />
      <Typography textAlign="center" variant="h4">Create a new role</Typography>
      <Typography textAlign="center">Define a new role with custom permissions</Typography>
    </Box>
  </ButtonRoleBase>
);

const PermissionRolesListContent = ({
  isEdit,
  selectedRole,
  setSelectedRole,
}: {
  isEdit: boolean;
  selectedRole: string | undefined;
  setSelectedRole: (newRole: string | undefined) => void;
}) => {
  const { data, isLoading } = useListController<PermissionRole>();

  useEffect(() => {
    if (data?.length && !selectedRole) setSelectedRole(data[0].id);
  }, [data, selectedRole, setSelectedRole]);
  const { permissions } = usePermissions<Actions[]>();

  if (isLoading) {
    return (
      <Box
        display="flex"
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {(data ?? []).map((permissionRole) => (
        <ButtonRole
          isSelected={selectedRole === permissionRole.id}
          key={permissionRole.id}
          onClick={() => setSelectedRole(permissionRole.id)}
          disabled={selectedRole !== permissionRole.id && isEdit}
          title={permissionRole.name}
          subtitle={truncate(permissionRole.description, { length: 95 })}
          iconName={permissionRole.iconName}
        />
      ))}
      {hasAccess(permissions, actions.PERMISSION_ROLE_CREATE) && (
        <CreateButton
          isSelected={selectedRole === CREATE_ROLE}
          onClick={() => setSelectedRole(CREATE_ROLE)}
          disabled={isEdit}
        />
      )}
    </>
  );
};

const SIZE_FIXED_ELEMENT = 199;

const PermissionRolesList = () => {
  const { permissionRoleId } = useParams();
  const [isEdit, setEdit] = useState(false);
  const theme = useTheme();
  const breakpoint = theme.breakpoints.up('lg');
  const isLargeScreen = useMediaQuery(breakpoint);
  const navigate = useNavigate();

  const refRightPanel = useRef<Element>();

  const updateRole = useCallback((newRole: string | undefined) => {
    navigate(newRole ?? '');
    if (refRightPanel && refRightPanel.current?.scrollTo) refRightPanel.current.scrollTo(0, 0);
  }, [navigate]);

  const outletContext = useMemo(() => ({
    isEdit,
    setEdit,
    setSelectedRole: updateRole,
  }), [isEdit, updateRole]);

  return (
    <Grid container spacing={4} padding={4}>
      <Grid
        size={{
          xs: 12,
          lg: 3,
        }}
        sx={{
          [breakpoint]: {
            maxWidth: 340,
          },
        }}
      >
        <Box
          display="flex"
          flexDirection={isLargeScreen ? 'column' : 'row'}
          overflow="auto"
          rowGap={4}
          columnGap={4}
          height={isLargeScreen ? `calc(100dvh - ${SIZE_FIXED_ELEMENT}px)` : undefined}
        >
          <ListBase>
            <PermissionRolesListContent
              selectedRole={permissionRoleId}
              setSelectedRole={updateRole}
              isEdit={isEdit}
            />
          </ListBase>
        </Box>
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 'grow',
        }}
      >
        <Box
          height={isLargeScreen ? `calc(100dvh - ${SIZE_FIXED_ELEMENT}px)` : undefined}
          overflow="auto"
          ref={refRightPanel}
        >
          <Outlet context={outletContext satisfies OutletContext} />
        </Box>
      </Grid>
    </Grid>
  );
};

export default PermissionRolesList;
